// components/i18n/Currency.tsx
import React from 'react';
import { useCountry } from '@/contexts/CountryContext';

interface CurrencyProps {
  amount: number;
  showSymbol?: boolean;
  className?: string;
  currencyCode?: string; // Override the context currency with specific currency
  currencySymbol?: string; // Override the context currency symbol
}

/**
 * Currency component that automatically formats amounts based on the current country context
 * Replaces hard-coded £ symbols throughout the application
 */
export const Currency: React.FC<CurrencyProps> = ({ 
  amount, 
  showSymbol = true, 
  className = '',
  currencyCode,
  currencySymbol
}) => {
  const { formatCurrency, getCurrencySymbol, currentCountry } = useCountry();

  // Use provided currency info or fall back to context
  const effectiveCurrencyCode = currencyCode || currentCountry?.currencyCode || 'GBP';
  const effectiveCurrencySymbol = currencySymbol || getCurrencySymbol();

  // Debug logging
  if (amount > 0) {  // Only log for actual amounts to avoid spam
    console.log('[Currency] Debug:', {
      amount,
      providedCurrencyCode: currencyCode,
      currentCountryCurrencyCode: currentCountry?.currencyCode,
      effectiveCurrencyCode,
      effectiveCurrencySymbol,
      currentCountry: currentCountry?.code
    });
  }

  // Format currency using the effective currency information
  const formatAmount = (amount: number): string => {
    try {
      // Use Intl.NumberFormat for proper currency formatting
      return new Intl.NumberFormat('en-GB', {
        style: 'currency',
        currency: effectiveCurrencyCode,
      }).format(amount);
    } catch (error) {
      // Fallback to simple formatting
      return `${effectiveCurrencySymbol}${amount.toFixed(2)}`;
    }
  };

  // If showSymbol is false, just show the number formatted without currency symbol
  if (!showSymbol) {
    const formatted = formatAmount(amount);
    // Remove the currency symbol and extra spaces
    const numberOnly = formatted.replace(/[£$€¥₹]/g, '').trim();
    return <span className={className}>{numberOnly}</span>;
  }

  return (
    <span className={className}>
      {formatAmount(amount)}
    </span>
  );
};

interface CurrencyInputProps {
  value: number;
  onChange: (value: number) => void;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
}

/**
 * Currency input component that shows the appropriate currency symbol as a prefix
 */
export const CurrencyInput: React.FC<CurrencyInputProps> = ({
  value,
  onChange,
  placeholder,
  className = '',
  disabled = false,
}) => {
  const { getCurrencySymbol } = useCountry();
  const symbol = getCurrencySymbol();

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;
    const numericValue = parseFloat(inputValue) || 0;
    onChange(numericValue);
  };

  return (
    <div className={`relative ${className}`}>
      <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500">
        {symbol}
      </span>
      <input
        type="number"
        value={value || ''}
        onChange={handleChange}
        placeholder={placeholder}
        disabled={disabled}
        className="pl-8 pr-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 w-full"
        step="0.01"
        min="0"
      />
    </div>
  );
};

export default Currency; 